import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Pagination,
} from "@nextui-org/react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StringLimit } from "../../controllers/APIController";
import {
  DeleteOnedata,
  getDataWithFilterUrl,
} from "../../controllers/APIController";

import { DeleteTableItem, SearchInput } from "../../utils/helper";
import LoadingPage from "../../components/Loader/LoadingPage";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import NullData from "../../components/ErrorPage/NullData";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const SurveyPlayground = () => {
  const navigate = useNavigate();
  const { _orgId } = useParams();
  const [currentPage, setCurrentPage] = useState(1);

  const [metrics, setMetrics] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const queryClient = useQueryClient();

  const UserLogged = JSON.parse(sessionStorage?.getItem("userData"));
  const userRole = UserLogged?.user?.userRole;

  const OrgInfo = UserLogged?.user?.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + OrgInfo;

  const handleNavigate = () => {
    navigate(`/${_orgId}/data-Questionaire/create`);
  };

  const deleteFormHandler = async (id) => {
    try {
      await DeleteOnedata("metrics", id);
      queryClient?.invalidateQueries({ queryKey: ["survey"] });
    } catch (error) {
      console.log(error);
    }
  };

  const { data: surveyData, isLoading } = useQuery({
    queryKey: ["survey"],
    queryFn: () =>
      getDataWithFilterUrl(
        "metrics",
        `${orgFilterUrl}&filters[Category][$eqi]=Survey`
      ),
  });

  useEffect(() => {
    const list = surveyData?.data;
    list?.sort((a, b) => {
      if (
        a?.attributes?.Name?.trim()?.toLowerCase() <
        b?.attributes?.Name?.trim()?.toLowerCase()
      ) {
        return -1;
      }
      if (
        a?.attributes?.Name?.trim()?.toLowerCase() >
        b?.attributes?.Name?.trim()?.toLowerCase()
      ) {
        return 1;
      }
      return 0;
    });

    setMetrics(list);
  }, [surveyData]);

  const headers = [
    "Metric Name",
    "Category",
    "Recommended Frequency",
    "Description",
    "Actions",
  ];

  const handleNavigateToMetric = (id) => {
    navigate(`/${_orgId}/data-Questionaire/${id}`);
  };

  const filteredMetrics = metrics?.filter((item) => {
    const name = item?.attributes?.Name || "";
    const description = item?.attributes?.Description || "";
    const category = item?.attributes?.Category || "";
    const isSurvey = category?.toLowerCase() === "survey";
    const matchesSearchTerm =
      name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      category?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return isSurvey && matchesSearchTerm;
  });

  const ItemPerPage = 20;
  const totalPage = Math?.ceil(filteredMetrics?.length / ItemPerPage);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-6 shadow-sm">
        <div className="flex items-center justify-between max-w-full">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Data Questionnaire
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Create and manage surveys for your organization
            </p>
          </div>
          <Button
            color="primary"
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 font-medium"
            onClick={handleNavigate}
          >
            Create a new Survey
          </Button>
        </div>
      </div>

      <BreadCrumbsPage>
        <div className="max-w-7xl mx-auto px-8 py-8">
          {/* Survey Management Section */}
          {/* <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-8">
            <div>
              { <h2 className="text-lg font-semibold text-slate-900">Survey Management</h2>
              <p className="text-sm text-slate-600 mt-1">
                Create or edit surveys that would support your ESG Reporting. Build comprehensive survey frameworks to guide your sustainability initiatives.
              </p> }
            </div>
          </div> */}

          {/* Survey Repository Section */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    Survey Repository
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Browse and manage surveys
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <LoadingPage
                isLoading={isLoading}
                loadingText="Loading surveys..."
              >
                <div className="mt-4">
                  <SearchInput
                    searchText={searchTerm}
                    setSearchText={setSearchTerm}
                    placeholderText={"Search for an ESG metric or survey"}
                  />
                  <div className="mt-8">
                    <div className="">
                      <Table
                        removeWrapper
                        selectionMode="single"
                        aria-label="Example table with dynamic content"
                      >
                        <TableHeader>
                          {headers?.map((header, headerIndex) => (
                            <TableColumn key={headerIndex}>
                              {header}
                            </TableColumn>
                          ))}
                        </TableHeader>
                        <TableBody
                          emptyContent={
                            <NullData
                              title={`No survey found. Click on "create" to make a new survey`}
                            />
                          }
                        >
                          {filteredMetrics?.map((item, index) => {
                            const lowerPageLimit =
                              (currentPage - 1) * ItemPerPage;
                            const upperPageLimit = currentPage * ItemPerPage;
                            if (
                              index < lowerPageLimit ||
                              index >= upperPageLimit
                            )
                              return null;

                            return (
                              <TableRow
                                key={item?.id}
                                onClick={() => handleNavigateToMetric(item?.id)}
                                className="cursor-pointer"
                              >
                                <TableCell>
                                  {StringLimit(item?.attributes?.Name, 50) ||
                                    "--"}
                                </TableCell>
                                <TableCell>
                                  {item?.attributes?.Category || "--"}
                                </TableCell>
                                <TableCell>
                                  {item?.attributes?.Frequency || "--"}
                                </TableCell>
                                <TableCell>
                                  {(item?.attributes?.Description &&
                                    StringLimit(
                                      item?.attributes?.Description,
                                      80
                                    )) ||
                                    "--"}
                                </TableCell>
                                <TableCell>
                                  <DeleteTableItem
                                    userRole={userRole}
                                    deleteHandler={deleteFormHandler}
                                    item={item}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  {filteredMetrics?.length > 0 && (
                    <>
                      <div className="w-full mt-8 flex items-center justify-center">
                        <Pagination
                          showControls
                          total={totalPage}
                          color="secondary"
                          page={currentPage}
                          onChange={setCurrentPage}
                        />
                      </div>
                    </>
                  )}
                </div>
              </LoadingPage>
            </div>
          </div>
        </div>
      </BreadCrumbsPage>
    </div>
  );
};

export default SurveyPlayground;
